import { useQuery } from '@tanstack/react-query';
import { AlarmClock, BadgeCheck, CalendarClock, Wallet } from 'lucide-react';
import { Link } from 'react-router';
import { formatMoney, type FinanceOverviewDto } from '@cw/shared';
import { Panel } from '@/design-system/components/panel';
import { ActivityFeed } from '@/features/activity/activity-feed';
import { api } from '@/lib/api';
import { useActivityFeed } from '@/lib/projects';
import { useCan } from '@/lib/session';
import { useTasks } from '@/lib/tasks';
import { cn } from '@/lib/utils';

function Tile({
  to,
  icon,
  label,
  value,
  tone,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  value: string | number;
  tone?: 'danger' | 'warning';
}) {
  return (
    <Link
      to={to}
      className="cw-rise flex items-center gap-3 rounded-panel bg-raised/60 p-4 shadow-[inset_0_0_0_1px_var(--color-line)] transition-colors hover:bg-raised"
    >
      <span
        className={cn(
          'flex size-9 shrink-0 items-center justify-center rounded-[10px] bg-press text-fg-muted [&_svg]:size-[18px]',
          tone === 'danger' && 'bg-danger/12 text-danger',
          tone === 'warning' && 'bg-warning/12 text-warning',
        )}
      >
        {icon}
      </span>
      <span className="min-w-0">
        <span className="tabular block text-[20px] leading-tight font-semibold tracking-[-0.02em]">
          {value}
        </span>
        <span className="block truncate text-[12px] text-fg-subtle">{label}</span>
      </span>
    </Link>
  );
}

/** Pulso do estúdio na Home (SPEC §7): atrasos, próximas entregas, aprovações e dinheiro. */
export function Pulse() {
  const canTasks = useCan('tasks:read');
  const canFinance = useCan('finance:write');
  const manager = useCan('requests:triage');
  const overdue = useTasks({ assignee: manager ? undefined : 'me', due: 'overdue' });
  const week = useTasks({ assignee: manager ? undefined : 'me', due: 'week' });
  const approvals = useQuery({
    queryKey: ['approvals', 'pending-count'],
    queryFn: () => api.get<{ count: number }>('/approvals/pending-count'),
  });
  const fin = useQuery({
    queryKey: ['finance', 'overview', 'home'],
    queryFn: () => api.get<FinanceOverviewDto>('/finance/overview'),
    enabled: canFinance,
  });
  if (!canTasks) return null;
  const open = (l?: { status: { category: string } }[]) =>
    (l ?? []).filter((t) => !['done', 'approved', 'cancelled'].includes(t.status.category)).length;
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <Tile
        to="/projects/kanban"
        icon={<AlarmClock />}
        label={manager ? 'Demandas atrasadas' : 'Suas atrasadas'}
        value={open(overdue.data)}
        tone={open(overdue.data) ? 'danger' : undefined}
      />
      <Tile to="/calendar" icon={<CalendarClock />} label="Entregas nesta semana" value={open(week.data)} />
      <Tile
        to="/assets"
        icon={<BadgeCheck />}
        label="Aguardando o cliente"
        value={approvals.data?.count ?? 0}
        tone={approvals.data?.count ? 'warning' : undefined}
      />
      {canFinance ? (
        <Tile
          to="/finance/overview"
          icon={<Wallet />}
          label={`A receber · previsto ${formatMoney(fin.data?.forecastCents ?? 0)}/mês`}
          value={formatMoney(fin.data?.pendingCents ?? 0)}
        />
      ) : null}
    </div>
  );
}

export function RecentActivity() {
  const canRead = useCan('activity:read');
  const feed = useActivityFeed(['activity', 'org'], '', canRead);
  if (!canRead) return null;
  return (
    <Panel title="Atividade recente">
      <ActivityFeed feed={feed} emptyText="Nada aconteceu por aqui ainda." />
    </Panel>
  );
}
